import { Link, useParams } from 'react-router-dom'
import { canWriteArea } from '@wf/shared'
import { ApiError } from '../api/client'
import { useAuth } from '../app/auth'
import { useCustomer } from '../customers/useCustomers'
import { ESTIMATE_LABEL } from '../orders/estimateStatus'
import { useOrder, useSendEstimate } from '../orders/useOrders'
import { useSettings } from '../settings/useSettings'
import { BillingDocument, liveDocModel } from './BillingDocument'

/**
 * Kostenvoranschlag-Vorschau, -Versand & -Druck (Route /print/estimate/:id, auth-gated, ohne App-Shell).
 * Beliebig oft öffenbar, immer aus dem aktuellen Positionsbestand gerendert. Zwei Aktionen: „KV versenden"
 * (setzt estimateStatus 'sent' und liefert EINMALIG den Klartext-Freigabe-Token → öffentlicher Freigabe-
 * Link, bei Reload weg, da die DB nur den Hash hält) und „Drucken / als PDF" (druckbares KV-PDF). Nach
 * Kundenfreigabe ('approved') ist der KV GoBD-terminal → kein Sende-Knopf mehr (der Server lehnt ohnehin ab).
 */
export function EstimatePrintPage() {
  const { id: idParam } = useParams()
  const id = Number(idParam)
  const orderQuery = useOrder(id)
  const order = orderQuery.data?.order ?? null
  const customerQuery = useCustomer(order?.customerId ?? null)
  const settingsQuery = useSettings()
  const { user } = useAuth()
  const send = useSendEstimate(id)

  if (orderQuery.isLoading || settingsQuery.isLoading) {
    return <div className="muted">Kostenvoranschlag wird geladen…</div>
  }
  if (orderQuery.error || !order) {
    return (
      <div className="error" role="alert">
        Auftrag konnte nicht geladen werden.
      </div>
    )
  }

  const tasks = orderQuery.data?.tasks ?? []
  const parts = orderQuery.data?.parts ?? []
  const items = orderQuery.data?.items ?? []
  const billing = settingsQuery.data?.billing ?? null
  const customer = customerQuery.data?.customer ?? null
  const vehicle = customerQuery.data?.vehicles.find((candidate) => candidate.id === order.vehicleId) ?? null

  const estimateStatus = order.estimateStatus
  const canRechnung = user ? canWriteArea(user, 'rechnungen') : false
  // Der Klartext-Token kommt nur aus der Sende-Antwort (DB hält nur den Hash) → Link nur direkt nach dem Senden.
  const approvalUrl = send.data ? `${window.location.origin}/freigabe/${send.data.token}` : null

  return (
    <main className="wf-print" data-theme="light">
      <div className="wf-print__bar">
        <Link to={`/orders/${id}`} className="wf-btn wf-btn--ghost">
          ← Zurück
        </Link>
        <div className="wf-print__baractions">
          {estimateStatus !== 'approved' && canRechnung ? (
            <button type="button" className="wf-btn" disabled={send.isPending} onClick={() => send.mutate()}>
              {estimateStatus === 'none' ? 'KV versenden' : 'KV erneut senden'}
            </button>
          ) : null}
          <button type="button" className="wf-btn wf-btn--primary" onClick={() => window.print()}>
            Drucken / als PDF
          </button>
        </div>
      </div>

      <div className="wf-print__status">
        <span className={`wf-estimate__state wf-estimate__state--${estimateStatus}`}>
          {ESTIMATE_LABEL[estimateStatus] ?? estimateStatus}
        </span>
      </div>

      {approvalUrl ? (
        <label className="wf-print__link">
          <span className="wf-label">Freigabe-Link für den Kunden</span>
          <input
            className="wf-input"
            readOnly
            value={approvalUrl}
            aria-label="Freigabe-Link"
            onFocus={(event) => event.target.select()}
          />
        </label>
      ) : null}
      {send.error instanceof ApiError ? (
        <p className="error" role="alert">
          {send.error.message}
        </p>
      ) : null}

      <BillingDocument model={liveDocModel({ kind: 'estimate', order, tasks, parts, items, billing, customer, vehicle, draft: false })} />
    </main>
  )
}
